import httpClient from 'services/httpClient';

/**
 * Collaboration API (`/collaborations`) — record-scoped Read/Write access grants.
 * Additive to every module's own service; no existing endpoint is touched.
 * httpClient returns the `{ data, meta }` envelope and injects the auth token.
 *
 * `resource` is the module key the backend enforces on (mirrors the permission
 * catalog): 'applications' (Program), 'events', 'evaluations' (Evaluation
 * Process). `recordId` is the specific record's UUID.
 */
export const collaborationService = {
  // ── Owner side (per record) ──────────────────────────────────────────────
  listCollaborators: (resource, recordId) =>
    httpClient.get(`/collaborations/records/${resource}/${recordId}`),
  invite: (resource, recordId, email, permission = 'read', role = 'member', firstName = '', lastName = '', collaborationOrganizationId = null) =>
    httpClient.post(`/collaborations/records/${resource}/${recordId}`, {
      email,
      permission,
      role,
      first_name: firstName || undefined,
      last_name: lastName || undefined,
      collaboration_organization_id: collaborationOrganizationId || undefined,
    }),
  // Record ↔ organization links: whole organizations that collaborate on a
  // record (Program/Event), persisted independently of per-person invites.
  listRecordOrganizations: (resource, recordId) =>
    httpClient.get(`/collaborations/records/${resource}/${recordId}/organizations`),
  addRecordOrganization: (resource, recordId, collaborationOrganizationId) =>
    httpClient.post(`/collaborations/records/${resource}/${recordId}/organizations`, {
      collaboration_organization_id: collaborationOrganizationId,
    }),
  removeRecordOrganization: (resource, recordId, organizationId) =>
    httpClient.delete(`/collaborations/records/${resource}/${recordId}/organizations/${organizationId}`),
  changePermission: (collaborationId, permission) =>
    httpClient.patch(`/collaborations/${collaborationId}`, { permission }),
  changeRole: (collaborationId, role) =>
    httpClient.patch(`/collaborations/${collaborationId}/role`, { role }),
  resend: (collaborationId) =>
    httpClient.post(`/collaborations/${collaborationId}/resend`),
  revoke: (collaborationId) =>
    httpClient.post(`/collaborations/${collaborationId}/revoke`),
  remove: (collaborationId) =>
    httpClient.delete(`/collaborations/${collaborationId}`),

  // ── Collaborator side (global) ───────────────────────────────────────────
  myInvitations: (status = 'pending') =>
    httpClient.get('/collaborations/my-invitations', { params: { status } }),
  sharedWithMe: () => httpClient.get('/collaborations/shared-with-me'),
  // Collaborations I've sent (I'm the owner) with each collaborator's status.
  sentByMe: () => httpClient.get('/collaborations/sent'),
  // Compact active-grant list that drives the AuthContext `canCollaborate` gate.
  myGrants: () => httpClient.get('/collaborations/my-grants'),
  accept: (collaborationId) => httpClient.post(`/collaborations/${collaborationId}/accept`),
  decline: (collaborationId) => httpClient.post(`/collaborations/${collaborationId}/decline`),

  // ── Collaboration Organizations ───────────────────────────────────────────
  listOrganizations: (createdByMe = false) =>
    httpClient.get('/collaborations/organizations', {
      params: createdByMe ? { created_by_me: true } : undefined,
    }),
  createOrganization: (name, description = '') =>
    httpClient.post('/collaborations/organizations', {
      name,
      description: description || undefined,
    }),
  updateOrganization: (organizationId, name, description = '') =>
    httpClient.patch(`/collaborations/organizations/${organizationId}`, {
      name,
      description: description || undefined,
    }),
  // Every collaborator tagged to this organization, from every inviter —
  // not just the ones the signed-in user personally invited (sentByMe is
  // scoped to that).
  listOrganizationCollaborators: (organizationId) =>
    httpClient.get(`/collaborations/organizations/${organizationId}/collaborators`),
  // This organization's current roster across every collaboration GROUP it
  // belongs to (distinct by email) — used to pre-fill existing members when
  // reusing an organization in Create/Edit collaboration. Distinct from
  // listOrganizationCollaborators above (that's Program/Event-level invites).
  listOrganizationGroupMembers: (organizationId) =>
    httpClient.get(`/collaborations/organizations/${organizationId}/group-members`),
  // The Programs/Events this organization has been added to or invited on —
  // reverse of listRecordOrganizations. Powers the Org → Program/Event drill-down.
  listOrganizationRecords: (organizationId) =>
    httpClient.get(`/collaborations/organizations/${organizationId}/records`),
  // Invite someone straight into an organization — no Program/Event.
  inviteToOrganization: (organizationId, email, permission = 'read', role = 'member', firstName = '', lastName = '') =>
    httpClient.post(`/collaborations/organizations/${organizationId}/invite`, {
      email,
      permission,
      role,
      first_name: firstName || undefined,
      last_name: lastName || undefined,
    }),

  // ── Collaboration Groups (the named "Collaborations" container) ────────────
  // A higher-level entity than the per-record grants above: a named container
  // that owns organizations, programs and events. Mounted at
  // `/collaborations/groups`. All responses use the `{ data, meta }` envelope.
  listGroups: (params) => httpClient.get('/collaborations/groups', { params, cache: false }),
  createGroup: (payload) => httpClient.post('/collaborations/groups', payload),
  // Pre-check one email before staging it as a draft member — surfaces the
  // "already a Jury/Mentor/Founder" error at "Add organization" time (409).
  validateCollaboratorEmail: (email) =>
    httpClient.post('/collaborations/groups/collaborators/validate', { email }),
  getGroup: (id) => httpClient.get(`/collaborations/groups/${id}`, { cache: false }),
  updateGroup: (id, payload) => httpClient.patch(`/collaborations/groups/${id}`, payload),
  deleteGroup: (id) => httpClient.delete(`/collaborations/groups/${id}`),
  archiveGroup: (id) => httpClient.post(`/collaborations/groups/${id}/archive`),
  restoreGroup: (id) => httpClient.post(`/collaborations/groups/${id}/restore`),
  leaveGroup: (id) => httpClient.post(`/collaborations/groups/${id}/leave`),

  // Organizations & members on a group. An organization has many members
  // (users); `addGroupOrganization` body = { name, collaboration_organization_id?,
  // members: [{ contact_first_name, contact_last_name, email }] } — pass an
  // existing collaboration_organization_id to add members to an org already on
  // the collaboration.
  listGroupOrganizations: (id) => httpClient.get(`/collaborations/groups/${id}/organizations`),
  addGroupOrganization: (id, body) => httpClient.post(`/collaborations/groups/${id}/organizations`, body),
  // Edit an org — rename and/or add new members. `organizationId` is the
  // collaboration_organization id. body = { name?, members?: [...] }.
  updateGroupOrganization: (id, organizationId, body) =>
    httpClient.patch(`/collaborations/groups/${id}/organizations/${organizationId}`, body),
  // Remove a whole organization (all its members). `organizationId` is the
  // collaboration_organization id.
  removeGroupOrganization: (id, organizationId) =>
    httpClient.delete(`/collaborations/groups/${id}/organizations/${organizationId}`),
  // Change an existing member's role ('member' | 'group_admin') — owner-only.
  updateGroupMember: (id, memberId, body) =>
    httpClient.patch(`/collaborations/groups/${id}/members/${memberId}`, body),
  // Dependency preview: OTHER active collaborations that also include this member
  // — shown before removing them, so shared members are never silently dropped.
  memberDependencies: (id, memberId) =>
    httpClient.get(`/collaborations/groups/${id}/members/${memberId}/dependencies`, { cache: false }),
  // Programs/Events a specific member can currently access — each Revoke-able.
  memberRecordAccess: (id, memberId) =>
    httpClient.get(`/collaborations/groups/${id}/members/${memberId}/records`, { cache: false }),
  // Revoke ONE member's access to ONE program/event. `resource` is
  // 'applications' (Program) or 'events'.
  revokeMemberRecord: (id, memberId, resource, recordId) =>
    httpClient.delete(`/collaborations/groups/${id}/members/${memberId}/records/${resource}/${recordId}`),
  // Dependency preview: OTHER active collaborations that also use this org.
  organizationDependencies: (id, organizationId) =>
    httpClient.get(`/collaborations/groups/${id}/organizations/${organizationId}/dependencies`, { cache: false }),
  // Active collaborations a Program/Event is attached to — shown before
  // archiving/deleting the record from its own module. `resource` is
  // 'applications' (Program) or 'events'.
  recordCollaborations: (resource, recordId) =>
    httpClient.get(`/collaborations/groups/for-record/${resource}/${recordId}`, { cache: false }),
  // Remove a single member (user). `memberId` is the member row id.
  removeGroupMember: (id, memberId) =>
    httpClient.delete(`/collaborations/groups/${id}/members/${memberId}`),
  resendGroupInvitation: (id, memberId) =>
    httpClient.post(`/collaborations/groups/${id}/members/${memberId}/resend`),
  // Revoke an ACCEPTED member's access — keeps them in the list as 'revoked'
  // (Resend re-invites); distinct from removeGroupMember (removes entirely).
  revokeGroupMemberAccess: (id, memberId) =>
    httpClient.post(`/collaborations/groups/${id}/members/${memberId}/revoke`),

  // Merged invitations (received + sent)
  listGroupInvitations: (params) =>
    httpClient.get('/collaborations/groups/invitations', { params, cache: false }),
  // Pending Program/Event assignment invitations for the current user (accepting
  // one grants access to that specific record → it appears in their module).
  listMyRecordInvitations: () =>
    httpClient.get('/collaborations/groups/my-record-invitations', { cache: false }),
  acceptGroupInvitation: (invId) =>
    httpClient.post(`/collaborations/groups/invitations/${invId}/accept`),
  declineGroupInvitation: (invId) =>
    httpClient.post(`/collaborations/groups/invitations/${invId}/decline`),
  revokeGroupInvitation: (invId) =>
    httpClient.post(`/collaborations/groups/invitations/${invId}/revoke`),

  // Records (Programs/Events) on a group
  listGroupPrograms: (id, params) =>
    httpClient.get(`/collaborations/groups/${id}/programs`, { params, cache: false }),
  listGroupEvents: (id, params) =>
    httpClient.get(`/collaborations/groups/${id}/events`, { params, cache: false }),
  addGroupRecord: (id, body) => httpClient.post(`/collaborations/groups/${id}/records`, body),
  removeGroupRecord: (id, resource, recordId) =>
    httpClient.delete(`/collaborations/groups/${id}/records/${resource}/${recordId}`),
  // Program ids already attached to a collaboration — the '+ Add program' picker
  // hides these (a program may belong to only one collaboration).
  listAssignedProgramIds: () =>
    httpClient.get('/collaborations/groups/assigned-program-ids', { cache: false }),

  // Manage Collaborators [H] — per-program/event assignment (assignment-based
  // access). `resource` is 'applications' (Program) or 'events'.
  listRecordCollaborators: (id, resource, recordId) =>
    httpClient.get(`/collaborations/groups/${id}/records/${resource}/${recordId}/collaborators`, { cache: false }),
  assignRecordCollaborator: (id, resource, recordId, body) =>
    httpClient.post(`/collaborations/groups/${id}/records/${resource}/${recordId}/collaborators`, body),
  resendRecordAssignment: (id, resource, recordId, assignmentId) =>
    httpClient.post(`/collaborations/groups/${id}/records/${resource}/${recordId}/collaborators/${assignmentId}/resend`),
  acceptRecordAssignment: (id, resource, recordId, assignmentId) =>
    httpClient.post(`/collaborations/groups/${id}/records/${resource}/${recordId}/collaborators/${assignmentId}/accept`),
  declineRecordAssignment: (id, resource, recordId, assignmentId) =>
    httpClient.post(`/collaborations/groups/${id}/records/${resource}/${recordId}/collaborators/${assignmentId}/decline`),
  unassignRecordCollaborator: (id, resource, recordId, assignmentId) =>
    httpClient.delete(`/collaborations/groups/${id}/records/${resource}/${recordId}/collaborators/${assignmentId}`),
};

export default collaborationService;
